"use client";

import type { AssistantStaticBubbleVariantProps } from "@ui/components/core/BaseChat";
import { assistantStaticBubbleVariants } from "@ui/components/core/BaseChat";
import IconCircleCheck from "@ui/central-icons/icons/IconCircleCheck";
import IconCircleX from "@ui/central-icons/icons/IconCircleX";
import { clsx } from "clsx";
import { LoaderCircle } from "lucide-react";
import { AnimatePresence, animate as startAnimation, type Transition } from "motion/react";
import * as m from "motion/react-m";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import type { ChatPartCompleteProps, ChatScenarioMedia, ChatToolPart } from "../types";
import { noop } from "../utils";

const TOOL_CALL_DURATION_SECONDS = 1.2;
const TOOL_CALL_TRANSITION: Transition = { duration: 0.3, ease: "easeOut" };
const defaultAssistantMessageVariants: AssistantStaticBubbleVariantProps = { variant: "cream" };

type ToolCallPartStatus = "loading" | "success" | "error";

function getToolCallStatus(
  successText?: string,
  errorText?: string,
  successImage?: ChatScenarioMedia | null,
): ToolCallPartStatus {
  if (successText || successImage) {
    return "success";
  }

  if (errorText) {
    return "error";
  }

  return "loading";
}

function getTerminalToolCallText(
  status: ToolCallPartStatus,
  successText?: string,
  errorText?: string,
): string | undefined {
  switch (status) {
    case "success": {
      return successText;
    }
    case "error": {
      return errorText;
    }
    case "loading": {
      return undefined;
    }
  }
}

function ToolCallPartLayout({
  assistantMessageVariants = defaultAssistantMessageVariants,
  icon,
  text,
  textClassName,
}: {
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
  icon: ReactNode;
  text: ReactNode;
  textClassName: string;
}): JSX.Element {
  return (
    <div className={assistantStaticBubbleVariants({ ...assistantMessageVariants, rounded: "pill" })}>
      <div className='tw-relative tw-flex tw-size-10 tw-flex-none tw-items-center tw-justify-center'>{icon}</div>
      <div className={clsx("tw-min-w-0 tw-truncate", textClassName)}>{text}</div>
    </div>
  );
}

function LoadingToolCallIcon(): JSX.Element {
  return (
    <div className='tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-animate-spin'>
      <LoaderCircle className='tw-size-5' strokeWidth={2} />
    </div>
  );
}

function DefaultSuccessToolCallIcon(): JSX.Element {
  return <IconCircleCheck className='tw-size-5 tw-text-green-600' />;
}

function ErrorToolCallIcon(): JSX.Element {
  return <IconCircleX className='tw-size-5 tw-text-red-600' />;
}

function renderSuccessImage(successImage: Exclude<ChatScenarioMedia, null | undefined>): JSX.Element {
  if (typeof successImage === "string") {
    return <Image alt='' className='tw-size-5 tw-object-contain' height={40} src={successImage} width={40} />;
  }

  return (
    <Image
      alt={successImage.alt ?? ""}
      blurDataURL={successImage.blurDataURL ?? undefined}
      className='tw-size-5 tw-object-contain'
      height={successImage.height ?? 40}
      placeholder={successImage.blurDataURL ? "blur" : "empty"}
      src={successImage.url ?? ""}
      width={successImage.width ?? 40}
    />
  );
}

function SuccessToolCallIcon({ successImage }: { successImage?: ChatScenarioMedia | null }): JSX.Element {
  if (successImage) {
    return renderSuccessImage(successImage);
  }

  return <DefaultSuccessToolCallIcon />;
}

function StaticToolCallPart({
  assistantMessageVariants = defaultAssistantMessageVariants,
  loadingText,
  successText,
  successImage,
  errorText,
}: {
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
  loadingText: string;
  successText?: string;
  successImage?: ChatScenarioMedia | null;
  errorText?: string;
}): JSX.Element {
  const status = getToolCallStatus(successText, errorText, successImage);
  const text = getTerminalToolCallText(status, successText, errorText) ?? loadingText;

  let icon: JSX.Element;

  switch (status) {
    case "loading": {
      icon = <LoadingToolCallIcon />;
      break;
    }
    case "success": {
      icon = <SuccessToolCallIcon successImage={successImage} />;
      break;
    }
    case "error": {
      icon = <ErrorToolCallIcon />;
      break;
    }
  }

  return (
    <ToolCallPartLayout
      icon={icon}
      text={text}
      textClassName='tw-mr-3.5 tw-truncate tw-text-[0.875rem]/5'
      assistantMessageVariants={assistantMessageVariants}
    />
  );
}

function StatusToolCallPart({
  assistantMessageVariants = defaultAssistantMessageVariants,
  loadingText,
  successText,
  successImage,
  errorText,
  onComplete,
}: {
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
  loadingText: string;
  successText?: string;
  successImage?: ChatScenarioMedia | null;
  errorText?: string;
  onComplete: () => void;
}): JSX.Element {
  const [hasReachedTerminalState, setHasReachedTerminalState] = useState(false);
  const onCompleteRef = useRef(onComplete);
  const hasCompletedRef = useRef(false);
  const terminalStatus = getToolCallStatus(successText, errorText, successImage);
  const terminalText = getTerminalToolCallText(terminalStatus, successText, errorText);

  const completeOnce = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    onCompleteRef.current();
  }, []);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    hasCompletedRef.current = false;
    setHasReachedTerminalState(false);

    const controls = startAnimation(0, 1, {
      duration: TOOL_CALL_DURATION_SECONDS,
      ease: "linear",
      onComplete: () => {
        if (terminalStatus === "loading") {
          completeOnce();
          return;
        }

        setHasReachedTerminalState(true);
      },
    });

    return () => {
      controls.stop();
    };
  }, [completeOnce, terminalStatus]);

  return (
    <ToolCallPartLayout
      assistantMessageVariants={assistantMessageVariants}
      icon={
        <AnimatePresence mode='wait' initial={false}>
          {hasReachedTerminalState && terminalStatus !== "loading" ? (
            <m.div
              key={terminalStatus}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={TOOL_CALL_TRANSITION}
              onAnimationComplete={completeOnce}
              className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
            >
              {terminalStatus === "success" ? (
                <SuccessToolCallIcon successImage={successImage} />
              ) : (
                <ErrorToolCallIcon />
              )}
            </m.div>
          ) : (
            <m.div
              key='loading'
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={TOOL_CALL_TRANSITION}
              className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'
            >
              <LoadingToolCallIcon />
            </m.div>
          )}
        </AnimatePresence>
      }
      text={
        <>
          <m.div
            initial={false}
            animate={{ opacity: hasReachedTerminalState && terminalStatus !== "loading" ? 0 : 1 }}
            transition={TOOL_CALL_TRANSITION}
            className='tw-col-start-1 tw-row-start-1 tw-truncate tw-text-[0.875rem]/5 tw-text-gray-400'
          >
            {loadingText}
          </m.div>

          {terminalText && (
            <m.div
              initial={false}
              animate={{ opacity: hasReachedTerminalState ? 1 : 0 }}
              transition={TOOL_CALL_TRANSITION}
              onAnimationComplete={() => {
                if (hasReachedTerminalState) {
                  completeOnce();
                }
              }}
              className='tw-col-start-1 tw-row-start-1 tw-truncate tw-text-[0.875rem]/5'
            >
              {terminalText}
            </m.div>
          )}
        </>
      }
      textClassName='tw-mr-3.5 tw-grid'
    />
  );
}

interface AssistantToolPartProps {
  part: ChatToolPart;
  onComplete?: ChatPartCompleteProps["onComplete"];
  animate?: boolean;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
}

export function AssistantToolPart({
  part,
  onComplete = noop,
  animate = true,
  assistantMessageVariants = defaultAssistantMessageVariants,
}: AssistantToolPartProps): JSX.Element {
  if (part.name === "simple") {
    if (!animate) {
      return <StaticToolCallPart {...part.data} assistantMessageVariants={assistantMessageVariants} />;
    }

    return (
      <StatusToolCallPart {...part.data} onComplete={onComplete} assistantMessageVariants={assistantMessageVariants} />
    );
  }

  const CustomToolComponent = part.data.component;

  return <CustomToolComponent onComplete={onComplete} />;
}
